<template>
  <!-- System Dialog Stack (Top Center) -->
  <!-- <div class="fixed top-2 left-1/2 -translate-x-1/2 z-40"> -->
  <div class="fixed top-0 left-0 min-w-72 z-40">
    <TransitionGroup
      enter-active-class="transform ease-out duration-300 transition"
      enter-from-class="translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-2 opacity-0"
      tag="div"
    >
      <div
        v-for="dialog in store.dialogs"
        :key="dialog.id"
        class="rounded-lg bg-purple-100 shadow-sm border mb-2 cursor-pointer"
        @click="removeDialog(dialog.id)"
      >
        <div class="flex justify-between items-center p-1">
          <div class="w-full text-center relative">
            <button
              @click.stop="removeDialog(dialog.id)"
              class="absolute -top-1 right-0 rounded-md  text-gray-400 hover:text-gray-500 flex items-center"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
            <h3 v-if="dialog.title" class="text-lg font-medium  break-words">
              {{ dialog.title }}
            </h3>
            <p class="m-2 text-base  break-words">
              {{ dialog.message }}
            </p>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>

  <!-- Notification Dialog Stack (Top Right) -->
  <div :class="[
    'fixed z-40 whitespace-nowrap flex flex-col',
    isMobile ? 'top-0 left-0 right-0 w-full' : 'top-2 right-12 w-[500px]'
  ]" v-if="store.showNotifications && unreadNotificationsCount() >= 1">
    <!-- Header with buttons -->
    <div class="flex justify-between items-center p-2 bg-white border-b">
      <div class="flex gap-2">
        <button 
          @click="toggleNotifications()"
          class="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-full border"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <button 
          @click="markAllNotificationsAsRead(allUnreadNotifications())"
          class="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-full border"
        >
          Mark All as Read
        </button>
      </div>
      <button 
        @click="isExpanded = !isExpanded"
        class="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-full border flex items-center gap-2"
      >
        {{ isExpanded ? 'Show less' : 'Show more' }}
      </button>
    </div>

    <!-- Scrollable notifications container -->
    <div class="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent bg-white" :class="{ 'max-h-[90vh]': isExpanded }">
      <TransitionGroup
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-0"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
        tag="div"
      >

      <!-- <TransitionGroup
        list-enter-active-class="transition ease-out duration-300"
        list-leave-active-class="transition ease-in duration-0"

        list-enter-from-class="opacity-0"
        list-leave-from-class="opacity-100"

        list-enter-to-class="opacity-100"
        list-leave-to-class="opacity-0"

        tag="div"
      > -->
      <!-- Loop through notifications bg-[#f5fafa] -->
        <div
          v-for="notification in displayedNotifications()"
          :key="notification.id"
          class="h-[130px] rounded-xl shadow-sm bg-[#f5fafa] border-4 mb-2"
        >
          <div v-if="notification.type === 'price_notification'" class="flex h-full p-2 bg-white rounded-xl">
            <!-- Left side icon -->
            <div class="flex justify-center items-center w-8 min-w-8 border-r-2 border-gray-200 p-1">
              <img 
                :src="getNotificationIcon(notification.subscriptionTypeConfig.type)" 
                class=""
                alt="Notification icon"
              />
            </div>
            
            <!-- Main content -->
            <div class="w-11/12 px-2 pt-1">
              <div class="flex flex-col h-full">
                <div class="flex justify-between items-start">
                  <div>
                    <!-- <div class="text-xl font-light text-center">
                     {{notification.subscriptionScope}} Alert, {{notification.subscriptionTypeConfig.type}}
                    </div> -->
                    <!-- Market Question -->
                    <div class="text-base font-light whitespace-normal text-gray-900">
                      '<a :href="`https://polymarket.com/event/${notification.polymarketSlug}`" target="_blank"><strong class="underline">{{ notification.marketQuestion }}</strong></a>' is <span class="font-bold">{{ getDetectedValue(notification) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Alert Type -->
                <div class="text-xs text-gray-600 mt-2 flex justify-center">
                  <span class="truncate w-3/4">{{ notification.triggerType }} '<strong>{{ notification.subscriptionTypeConfig.type }} {{ getConfigValue(notification) }}%</strong>' {{ notification.subscriptionScope }} alert in <span class="font-semibold" v-tippy="{ content: getWatchlistName(notification) }">{{ getWatchlistName(notification) }}</span>
                  </span>
                  <a v-if="false" href="#" class="ml-2 underline" @click="editAlert(notification)">(Edit)</a>
                </div>
                
                <div class="mt-auto flex justify-between items-center relative">
                   <!-- Detection Time and Created At -->
                  <div class="flex-1">

                    <!-- Detection Time -->
                    <div v-if="notification.readFromUserAtMs" class="text-xs text-gray-400">
                      Delay <span class="font-bold">{{ getDetectionTime(notification) }}ms</span>
                    </div>

                    <!-- Created At -->
                    <div v-if="notification.createdAt" 
                    class="text-xs text-gray-400"
                    v-tippy="{content: notification.createdAt}">
                      {{ formatDistanceToNow(notification.createdAt) }}
                    </div>
                  </div>

                  <!-- Pause Alert Button -->
                  <button v-if="false"
                    @click="pauseAlert(notification)"
                    class="absolute font-light left-1/2 -translate-x-1/2 px-2 py-1 text-xs bg-white rounded-full hover:bg-gray-200 text-gray-700">
                    Pause Alert <i class="fa-solid fa-pause"></i>
                  </button>

                  <!-- Mark as Read Button -->
                  <div class="flex-1 flex justify-end">
                    <button
                      @click.stop="markNotificationAsRead(notification.id)"
                      class="text-gray-400 hover:text-gray-500"
                    >
                      <i class="fa-solid fa-check"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Event Status Notification -->
          <div v-else class="flex h-full p-2 bg-white rounded-xl">
            <!-- Left side icon -->
            <div class="flex justify-center items-center w-8 min-w-8 border-r-2 border-gray-200 p-1">
              <img 
                :src="getNotificationIcon(SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES)" 
                class=""
                alt="Notification icon"
              />
            </div>

            <!-- Main content -->
            <div class="w-full px-2 pt-1 flex flex-col justify-between h-full">
              <div class="text-base font-light text-start whitespace-normal text-gray-900">
                <div v-if="notification.eventStatusScope === EventStatusScope.EVENT">
                  {{ notification.eventStatusScope }} '<a class="underline font-bold" :href="`https://polymarket.com/event/${notification.polymarketSlug}`" target="_blank">{{ getEventStatusTitle(notification) }}</a>' is <span class="font-bold">{{ notification.triggerType }}.</span>
                  <br><span class="text-xs text-gray-500 font-bold" >{{ getOutcomes(notification) }}</span>
                </div>
                <div v-if="notification.eventStatusScope === EventStatusScope.MARKET">
                  {{ notification.eventStatusScope }} <a class="underline font-bold" :href="`https://polymarket.com/event/${notification.polymarketSlug}`" target="_blank">{{ getEventStatusTitle(notification) }}</a> is <span class="font-bold">{{ notification.triggerType }}.</span>
                  <br><span class="text-xs text-gray-500 font-bold" >{{ getOutcomes(notification) }}</span>
                </div>
              </div>
              <!-- Bottom row with timestamp and checkmark -->
              <div class="flex justify-between items-center mt-2">
                <!-- Timestamp -->
                <div v-if="notification.createdAt" 
                  class="text-xs text-gray-400"
                  v-tippy="{content: notification.createdAt}">
                  {{ formatDistanceToNow(notification.createdAt) }}
                </div>
                <!-- Checkmark button -->
                <button
                  @click.stop="markNotificationAsRead(notification.id)"
                  class="text-gray-400 hover:text-gray-500"
                >
                  <i class="fa-solid fa-check"></i>
                </button>
              </div>
            </div>

          </div>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<script>
import ms from 'ms';
import { TransitionGroup } from 'vue'
import { 
  store, 
  removeDialog, 
  toggleNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  unreadNotificationsCount,
  pauseAlertInAPI,
  getOutcomes
} from '../store'
import { SubscriptionType } from '../types/Subscriptions'
import { NotificationChannelType, EventStatusScope } from '../types/Notifications'
import { formatDistanceToNowStrict } from 'date-fns';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';

export default {
  components: { 
    TransitionGroup
  },
  data() {
    return {
      store,
      SubscriptionType,
      EventStatusScope,
      isExpanded: false,
      hideTimeout: null,
      isPausingAlertId: null,
      breakpoints: useBreakpoints(breakpointsTailwind)
    }
  },
  computed: {
    isMobile() {
      return this.breakpoints.smallerOrEqual('sm').value
    }
  },
  watch: {
    'store.showNotifications'(newVal) {
      if (newVal && !store.notificationsManuallyToggled) {
        // If notifications are shows and not manually toggled, reset the hide timer
        this.resetHideTimer();
      }
      // Reset the manual toggle flag when notifications are hidden
      if (!newVal) {
        store.notificationsManuallyToggled = false;
      }
    },
    'store.notifications': {
      handler(newVal, oldVal) {
        // If a new notification was added
        if (newVal.length > (oldVal?.length || 0)) {
          store.showNotifications = true;
          if (!store.notificationsManuallyToggled) {
            // autohide only if the user hasn't manually toggled notifications
            this.resetHideTimer();
          }
        } else if (newVal.length <= 1) {
          this.isExpanded = false;
        }
      },
      deep: true
    }
  },

  methods: {
    unreadNotificationsCount,
    toggleNotifications,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    removeDialog,

    getOutcomes,
    getNotificationIcon(type) {
      switch (type) {
        case SubscriptionType.GREATER_THAN:
          return '/assets/alerts/greater-than.svg'
        case SubscriptionType.LESS_THAN:
          return '/assets/alerts/less-than.svg'
        case SubscriptionType.CROSSING:
          return '/assets/alerts/crossing.svg'
        case SubscriptionType.CROSSING_UP:
          return '/assets/alerts/crossing-up.svg'
        case SubscriptionType.CROSSING_DOWN:
          return '/assets/alerts/crossing-down.svg'
        case SubscriptionType.PCT_MOVE:
          return '/assets/alerts/pct-move.svg'
        case SubscriptionType.PCT_MOVE_UP:
          return '/assets/alerts/pct-up.svg'
        case SubscriptionType.PCT_MOVE_DOWN:
          return '/assets/alerts/pct-down.svg'
        case SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES:
          return '/assets/alerts/event-status.svg'
        default:
          return '/assets/alerts/pct-up.svg'
      }
    },

    getConfigValue(notification) {
      return notification.subscriptionTypeConfig.config.params.threshold_percent
    },

    getDetectedValue(notification) {
      return `${(notification.detectedValue * 100).toFixed(2)}%`
    },

    getDetectionTime(notification) {
      if (notification.readFromUserAtMs) {
        return notification.readFromUserAtMs - notification.sentFromPolymarketAtMs
      }
      return null
    },

    getWatchlistName(notification) {
      const watchlist = this.store.watchLists.find(watchlist => watchlist.id === notification.watchlistId)
      if (!watchlist) {
        Sentry.captureException(`Watchlist not found for notification ${notification.id} and watchlistId ${notification.watchlistId}`);
        return ''
      }
      return watchlist.name
    },

    getEventStatusTitle(notification) {
      if (notification.eventStatusScope === EventStatusScope.EVENT) {
        return notification.eventTitle;
      } else {
        if (notification.marketQuestion === notification.eventTitle) {
          return notification.marketQuestion;
        } else {
          return `${notification.eventTitle}: ${notification.marketQuestion}`;
        }
      }
    },

    allUnreadNotifications() {
      return this.store.notifications.filter(notification => notification.channels.some(channel => channel.status !== 'READ' && channel.type === NotificationChannelType.IN_APP));
    },
    displayedNotifications() {
      const unreadNotifications = this.allUnreadNotifications();

      if (!this.isExpanded && unreadNotifications.length > 1) {
        return [unreadNotifications[0]]
      }
      return unreadNotifications
    },

    resetHideTimer() {
      // Clear any existing timeout
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
      // Set new timeout
      this.hideTimeout = setTimeout(() => {
        if (this.hideTimeout) {
          store.showNotifications = false;
        }
      }, store.notificationTimeout || 2000);
    },

    formatDistanceToNow(dateString) {
      if (!dateString) return '';
      try {
        const distance = formatDistanceToNowStrict(new Date(dateString), { addSuffix: true });
        return distance === '0 seconds ago' ? 'now' : distance;
      } catch (error) {
        console.warn(`Invalid date format for: ${dateString}`);
        return '';
      }
    },

    async pauseAlert(notification) {
      this.isPausingAlertId = notification.id;
      await pauseAlertInAPI(notification);
      this.isPausingAlertId = null;
    },

    editAlert(notification) {
      console.log('editAlert', notification);
      this.$router.push({ 
        path: `/l/${notification.watchlistId}`,
        meta: { editAlertId: notification.subscriptionId }
      });
    }
  },
  beforeUnmount() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }
}
</script> 